/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  FilteredDataQuery,
  getSlicedData,
  optimisticLockingOf,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { LocationDto } from './location.dto';
import { LocationMapper } from './location.mapper';

@Injectable()
export class LocationRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  create(location: LocationDto): Observable<LocationDto> {
    return this.httpPost(`${this.bp}/locations`, location, optimisticLockingOf(LocationMapper));
  }

  update(location: LocationDto): Observable<LocationDto> {
    return this.httpPut(`${this.bp}/locations/${location.id}`, location, optimisticLockingOf(LocationMapper));
  }

  remove(location: LocationDto): Observable<LocationDto> {
    return this.httpDelete(`${this.bp}/locations/${location.id}`, location, optimisticLockingOf(LocationMapper));
  }

  get(id: ID): Observable<LocationDto> {
    return this.httpGet(`${this.bp}/locations/${id}`, optimisticLockingOf(LocationMapper));
  }

  findAllAsDataSource(): SlicedDataSource<LocationDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/locations`, arrayOf(LocationMapper));
  }

  findAll(): Observable<LocationDto[]> {
    return this.httpGet(`${this.bp}/locations`, arrayOf(LocationMapper));
  }

  findSlicedByCity(query: FilteredDataQuery<string>): Observable<LocationDto[]> {
    return this.createSlicedDataRequest({
      method: RequestMethod.Get,
      url: `${this.bp}/locations/autocomplete`,
      query,
      body: { city: query.filter },
      responseMapper: arrayOf(LocationMapper),
    }).pipe(map$(getSlicedData));
  }
}
